import { readStoredJson } from '@/utils/common';

// Fields only the sync wizard collects. Before syncs had their own catalog
// product the wizard tagged its cart item as a sample, and such an item still
// resolves to Sample Clear: it bills, and the clearance is written into the
// sample tables with sync fields and no sampled song. That is a wrong record
// rather than a refused purchase, so one stored before this change — a cart
// abandoned at the payment step outlives the deploy — is dropped on read, and
// the buyer runs the wizard again.
const SYNC_ONLY_FIELDS = ['track_link', 'project_title', 'description_of_use', 'publishing_or_master_sync_used'];

const isPreSyncCartItem = (item) =>
    Boolean(item) && item.product_type === 'sample' && SYNC_ONLY_FIELDS.some((field) => item[field] != null);

/**
 * The stored cart, always a list: a missing or unreadable value reads as empty
 * rather than throwing on the page that loads it.
 * @returns {Array}
 */
export const readCart = () => {
    const stored = readStoredJson('cart', []);
    return Array.isArray(stored) ? stored.filter((item) => !isPreSyncCartItem(item)) : [];
}

export const addToCart = (item) => {
    const cartContent = readCart();
    cartContent.push(item);
    localStorage.setItem('cart', JSON.stringify(cartContent));
    return cartContent;
}


const removeFromCart = () => {

}

export function clearCart() {
  localStorage.removeItem("cart"); // <-- replace 'cart' with your actual key
  // If you use Redux or other state, also dispatch an action here if needed!
}

export const TOP_UP_EXPIRY_NOTE = 'Credits are good until your plan renews.';

/**
 * Build the cart line for one top-up pack. product_id is the catalog id the API
 * prices from; priceId + product_type let checkout resolve the same id from the
 * catalog the older cart items rely on. Quantity is never set: the API charges
 * and credits one line per cart item, so buying two means two items.
 * @param {{id: number, name: string, priceId: string, price: number}} pack
 * @returns {Object} A cart item
 */
export const buildTopUpCartItem = (pack) => ({
    id: `topup-${pack.id}`,
    product_id: pack.id,
    priceId: pack.priceId,
    product_type: 'topup',
    plan_name: pack.name,
    plan_description: TOP_UP_EXPIRY_NOTE,
    price: pack.price,
});

/**
 * The catalog row a cart item was built from. A slug names the row outright —
 * memberships share a product type, and their price ids differ between the
 * sandbox and live Stripe accounts, so they carry one. Otherwise a price id
 * alone is not unique across product types, so both have to match. An item whose
 * price id the catalog does not carry — one baked in for the other environment,
 * or none at all for a product billed as an amount-only invoice line — resolves
 * on its product type instead, and only while that type has a single active row,
 * so an ambiguous type is refused rather than charged against whichever row came first.
 * @param {Array} [products] Catalog rows
 * @param {Object} item A cart item
 * @returns {(Object|null)}
 */
export const resolveCatalogProduct = (products, item) => {
    const ofType = (products || []).filter((product) => product.product_type === item.product_type);

    if (item.slug) return ofType.find((product) => product.slug === item.slug) || null;

    const byPrice = item.priceId && ofType.find((product) => product.stripe_price_id === item.priceId);
    if (byPrice) return byPrice;

    return ofType.length === 1 ? ofType[0] : null;
};

/**
 * What each cart line costs this user, in whole dollars, from the pricing in
 * GET /api/v2/me/entitlements — the same resolution the charge uses. A member
 * only gets a product's discount while they still hold a credit of its type,
 * and one credit cannot discount two lines, so the allowance is budgeted across
 * the cart exactly as the API budgets it. `base` is the list price, so a
 * discounted line can show what it would otherwise have cost. A line the
 * payload does not price — a membership, an unknown product, or any line for a
 * guest — is null, leaving the caller on the public catalog amount.
 * @param {Array} [cartItems] The cart
 * @param {Object} [entitlements] Mapped GET /me/entitlements payload
 * @returns {Array<({amount: number, base: number, discounted: boolean}|null)>} One price per cart item
 */
export const priceCartWithEntitlements = (cartItems, entitlements) => {
    const products = (entitlements && entitlements.products) || [];
    const remaining = { ...((entitlements && entitlements.credits) || {}) };

    return (cartItems || []).map((item) => {
        const pricing = (resolveCatalogProduct(products, item) || {}).pricing;
        if (!pricing) return null;

        const takesDiscount =
            pricing.credit_applies && (remaining[pricing.credit_type] || 0) > 0;

        if (!takesDiscount) {
            return { amount: pricing.base, base: pricing.base, discounted: false };
        }

        remaining[pricing.credit_type] -= 1;

        return {
            amount: pricing.yours,
            base: pricing.base,
            discounted: pricing.yours < pricing.base,
        };
    });
};
